/**
 * useEntitlements — React hook for auth state and feature gating.
 *
 * Wraps the AuthHubService singleton in React-friendly state. Exposes:
 * - isSignedIn:     true if the user has signed in via the DDD auth hub
 * - hasHostPass:    true if the user has DrawLab Host Pass
 * - user:           { displayName, photoURL } if signed in
 * - plan:           'free' | 'host-pass' | 'plus' | 'ultimate' etc.
 *
 * Plus the canonical feature gates used throughout the app:
 * - canHostBigRooms:      true if rooms with 4+ players are allowed
 * - canUsePremiumGameMode: (modeId) => true for free-tier modes always,
 *                         true for premium modes only with Host Pass
 *
 * Use `openPaywall` to trigger the upgrade flow from anywhere.
 */

import { useState, useEffect, useCallback } from 'react';
import authHub from '@/services/authHub';

// Free-tier game modes (always unlocked)
const FREE_GAME_MODES = new Set(['squiggle', 'corpse']);

// Max players in a free-tier room
export const FREE_TIER_MAX_PLAYERS = 3;

// Global paywall open state — shared across the app via a tiny event system.
// When any component calls openPaywall(), a PaywallModal subscribed to this
// signal will appear. Small and focused, no need for a full store.
let paywallListeners = new Set();
let paywallState = { open: false, trigger: null };

export function openPaywall(trigger = 'generic') {
  paywallState = { open: true, trigger };
  paywallListeners.forEach((cb) => cb(paywallState));
}

export function closePaywall() {
  paywallState = { open: false, trigger: null };
  paywallListeners.forEach((cb) => cb(paywallState));
}

export function usePaywallState() {
  const [state, setState] = useState(paywallState);
  useEffect(() => {
    paywallListeners.add(setState);
    return () => { paywallListeners.delete(setState); };
  }, []);
  return state;
}

export function useEntitlements() {
  const [isSignedIn, setIsSignedIn] = useState(authHub.isSignedIn());
  const [hasHostPass, setHasHostPass] = useState(authHub.hasHostPass());
  const [plan, setPlan] = useState(authHub.getSubscriptionPlan());
  const [displayName, setDisplayName] = useState(authHub.getDisplayName());
  const [photoURL, setPhotoURL] = useState(authHub.getPhotoURL());

  useEffect(() => {
    // Subscribe to auth state changes — updates whenever the user signs in
    // or their claims refresh (e.g. after a purchase)
    const unsubscribe = authHub.onAuthStateChanged(() => {
      setIsSignedIn(authHub.isSignedIn());
      setHasHostPass(authHub.hasHostPass());
      setPlan(authHub.getSubscriptionPlan());
      setDisplayName(authHub.getDisplayName());
      setPhotoURL(authHub.getPhotoURL());
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const canHostBigRooms = hasHostPass;

  const canUsePremiumGameMode = useCallback((modeId) => {
    if (!modeId) return true;
    if (FREE_GAME_MODES.has(modeId)) return true;
    return hasHostPass;
  }, [hasHostPass]);

  return {
    // Auth state
    isSignedIn,
    displayName,
    photoURL,
    plan,

    // Entitlements
    hasHostPass,
    canHostBigRooms,
    canUsePremiumGameMode,

    // Actions
    signIn: () => authHub.redirectToLogin(),
    signUp: () => authHub.redirectToSignup(),
    signOut: () => authHub.signOut(),
    manageProfile: () => authHub.redirectToProfile(),
    openPaywall,
  };
}
